import React from "react";
import axios from "axios";
import { BASE_URL } from "../Const/Url";
import { showToast } from "../../utils/ToastHelper";

export const GC_API = `${BASE_URL}api/v1/giftcards/`;

// A redeemed gift card rides in the invoice's mixed_payment_details list.
// The outlet POS writes this `value`; nothing else may claim it.
export const GIFT_CARD_MIX_VALUE = "giftcard";

// status -> label + gc-pill tone
export const GC_STATUS = {
  0: { label: "Inactive", tone: "neutral" },
  1: { label: "Active", tone: "ok" },
  2: { label: "Redeemed", tone: "info" },
  3: { label: "Disabled", tone: "crit" },
  4: { label: "Expired", tone: "warn" },
};

// Already-active / already-redeemed cards just fail bulk (and single) activate;
// keep them out of selection instead of letting staff pick a card that can
// only error.
export const ACTIVATABLE = (status) => status !== 1 && status !== 2;

// Soft-delete (and hard batch delete before it) is Super Admin only.
export const isSuperAdmin = () =>
  JSON.parse(localStorage.getItem("userData"))?.role === "Super Admin";

// The dot carries the tone, the word carries the meaning - never colour alone.
export const StatusPill = ({ status, label }) => {
  const meta = GC_STATUS[status] || {};
  return (
    <span className={`gc-pill gc-pill--${meta.tone || "neutral"}`}>
      {label || meta.label || "Unknown"}
    </span>
  );
};

export const money = (value) =>
  value === undefined || value === null
    ? "-"
    : `৳${Number(value).toLocaleString("en-BD", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

export const dateTime = (value) =>
  value ? new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }) : "-";

// transaction ledger kinds
export const GC_KIND = {
  0: "Activate",
  1: "Redeem",
  2: "Refund",
  3: "Adjust",
  4: "Reserve",
  5: "Release",
  6: "Pin Reset",
  7: "Disable",
};

// The backend writes `message` for customers already — show it as-is.
// Some newer endpoints (soft-delete) use `detail` instead of `message`.
export const showApiError = (err, fallback = "Something went wrong") => {
  try {
    const res = JSON.parse(err.request.response);
    showToast("error", res?.message || res?.detail || fallback);
  } catch {
    showToast("error", fallback);
  }
};

// Invoice lookup for the activate flow - two characters keeps it off every
// keystroke, and the list endpoint's `query` matches number and customer.
export const searchInvoices = (input) =>
  input.trim().length < 2
    ? Promise.resolve([])
    : axios
        .get(
          `${BASE_URL}api/v1/sales/inventory/invoice/?limit=20&query=${encodeURIComponent(
            input.trim()
          )}`
        )
        .then((res) =>
          (res.data?.data?.results || []).map((inv) => ({
            // the detail endpoint is keyed by slug, activate calls want the id
            value: inv.slug,
            label: inv.number,
            invoice: inv,
          }))
        )
        .catch(() => []);

// .xlsx endpoints are file responses, not JSON.
export const downloadXlsx = (axios, url, filename) =>
  axios
    .get(url, { responseType: "blob" })
    .then((res) => {
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    })
    .catch(() => showToast("error", "Export failed"));
